import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, CalendarDays, MapPin, User, Flag, Edit, Trash2, Phone } from "lucide-react";
import MessageButton from "@/components/MessageButton";
import SaveButton from "@/components/SaveButton";
import AdminRemoveButton from "@/components/AdminRemoveButton";

const LABEL = { special: "Special Event", community: "Community Event", church: "Church Event" };

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => setEvent(false));
  }, [id]);

  if (event === false) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Event not found.</div>;
  if (!event) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Loading…</div>;

  const isOwner = user?.id === event.owner_id;
  const start = new Date(event.start_date);
  const end = event.end_date ? new Date(event.end_date) : null;

  const remove = async () => {
    if (!confirm("Delete this event?")) return;
    await api.delete(`/events/${event.id}`);
    toast.success("Event deleted");
    nav("/events");
  };
  const submitReport = async () => {
    if (!reason.trim()) return toast.error("Please describe the reason");
    await api.post("/reports", { target_type: "event", target_id: event.id, reason, details });
    toast.success("Thanks for the report — we'll review this event.");
    setReason(""); setDetails("");
  };

  return (
    <div>
      <div className="relative h-[46vh] min-h-[300px] bg-[#1E3A2F] overflow-hidden">
        {event.images?.[0] && <img src={imageUrl(event.images[0])} alt={event.title} className="w-full h-full object-cover opacity-70" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A2F] via-[#1E3A2F]/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 max-w-6xl mx-auto px-5 sm:px-8 pb-8 text-[#F7F4EB]">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-[#DDA74A] mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
          <div className="abm-chip !bg-[#DDA74A]/20 !text-[#DDA74A] !border-[#DDA74A]/40">{LABEL[event.category]}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-4xl leading-tight">{event.title}</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-[1.4fr_0.6fr] gap-10">
        <div>
          <h2 className="font-display text-2xl text-[#1E3A2F]">About this event</h2>
          <p className="mt-4 whitespace-pre-wrap text-[#1A1A1A] leading-relaxed">{event.description}</p>
        </div>
        <aside className="lg:sticky lg:top-24 h-fit rounded-2xl bg-white border border-[#E2DED0] p-6 space-y-4">
          <InfoRow icon={<CalendarDays className="w-4 h-4" />} label="Date & time">
            <div>{start.toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}</div>
            {end && <div className="text-xs text-[#5C5C5C]">Ends {end.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</div>}
          </InfoRow>
          <InfoRow icon={<MapPin className="w-4 h-4" />} label="Location">
            <div>{event.location}</div>
            {event.address && <div className="text-xs text-[#5C5C5C]">{event.address}</div>}
          </InfoRow>
          {event.organizer && <InfoRow icon={<User className="w-4 h-4" />} label="Organizer">{event.organizer}</InfoRow>}
          {event.contact && <InfoRow icon={<Phone className="w-4 h-4" />} label="Contact">{event.contact}</InfoRow>}

          <div className="pt-3 border-t border-[#E2DED0] flex flex-wrap gap-2">
            {isOwner ? (
              <>
                <Button data-testid="event-edit-btn" className="abm-btn-pine rounded-full" onClick={() => nav(`/post/event/${event.id}`)}><Edit className="w-4 h-4 mr-1" /> Edit</Button>
                <Button data-testid="event-delete-btn" variant="destructive" className="rounded-full" onClick={remove}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
              </>
            ) : (
              <>
                <MessageButton
                  recipientId={event.owner_id}
                  recipientName={event.owner_name || event.organizer || "Organizer"}
                  targetType="event"
                  targetId={event.id}
                  targetTitle={event.title}
                  size="sm"
                />
                <SaveButton type="event" id={event.id} />
                <Dialog>
                  <DialogTrigger asChild>
                    <Button data-testid="event-report-btn" variant="outline" className="rounded-full"><Flag className="w-4 h-4 mr-1" /> Report</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Report this event</DialogTitle></DialogHeader>
                    <Input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="event-report-reason" />
                    <Textarea placeholder="Details (optional)" value={details} onChange={(e) => setDetails(e.target.value)} data-testid="event-report-details" />
                    <Button onClick={submitReport} className="abm-btn-primary rounded-full" data-testid="event-report-submit">Submit report</Button>
                  </DialogContent>
                </Dialog>
              </>
            )}
            <AdminRemoveButton type="event" id={event.id} title={event.title} redirectTo="/events" />
          </div>
        </aside>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C] mb-1 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="text-[#1A1A1A]">{children}</div>
    </div>
  );
}
